// src/pages/client/ProfilePage.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { userService } from '../../services/userService';
import '../../styles/ClientProfile.css';
import { 
  EditIcon, SaveIcon, CameraIcon, PhoneIcon, EmailIcon, DeleteIcon, 
  LoyaltyIcon, CloseIcon, 
  LogoutIcon, NotificationsIcon, SecurityIcon,
  SupportIcon
} from '../../components/common/Icons';

// Menu item interface
interface MenuItem {
  icon: React.ReactNode;
  title: string;
  path?: string;
  show?: boolean;
  onClick?: () => void;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get user from Redux store
  const { user } = useAppSelector(state => state.auth);
  
  // Component state
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      await userService.updateProfile({
        name: formData.name,
        phone: formData.phone
      });
      
      setEditMode(false);
      setSuccess('Profile updated successfully');
      setLoading(false);
      
      // In a real app, you would dispatch an action to update the user in Redux store
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
      setLoading(false);
    }
  };
  
  // Handle profile photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    try {
      setPhotoLoading(true);
      setError(null);
      
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('photo', file);
      
      await userService.updateProfilePhoto(formData);
      
      setSuccess('Profile photo updated successfully');
      setPhotoLoading(false);
      
      // In a real app, you would dispatch an action to update the user photo in Redux store
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Failed to upload profile photo');
      setPhotoLoading(false);
    }
  };
  
  // Handle profile photo deletion
  const handleDeletePhoto = async () => {
    try {
      setPhotoLoading(true);
      setError(null);
      
      await userService.deleteProfilePhoto();
      
      setSuccess('Profile photo removed successfully');
      setPhotoLoading(false);
      
      // In a real app, you would dispatch an action to remove the user photo in Redux store
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Failed to remove profile photo');
      setPhotoLoading(false);
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      setLoading(true);
      // Call logout service/action
      // await dispatch(logout());
      setLoading(false);
      // Redirect to login
      // navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to logout');
      setLoading(false);
    }
  };
  
  // Handle toast notification close
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  // Menu items based on mobile profile screen
  // Account Settings
  const accountSettings: MenuItem[] = [
    {
      icon: <EditIcon />,
      title: 'Edit Profile',
      path: '/client/edit-profile',
    },
    {
      icon: <NotificationsIcon />,
      title: 'Notifications',
      path: '/client/notifications',
    },
    {
      icon: <SecurityIcon />,
      title: 'Password & Security',
      path: '/client/security',
    },
  ];

  // Help & Support
  const helpSettings: MenuItem[] = [
    {
      icon: <SupportIcon />,
      title: 'Contact Support',
      path: '/client/support',
    },
    {
      icon: <LogoutIcon />,
      title: 'Logout',
      onClick: () => setShowLogoutConfirm(true),
    },
  ];

  // Render menu item
  const renderMenuItem = (item: MenuItem) => {
    if (item.show === false) return null;
    
    return (
      <div 
        key={item.title}
        className="menu-item"
        onClick={() => {
          if (item.path) {
            navigate(item.path);
          } else if (item.onClick) {
            item.onClick();
          }
        }}
      >
        <div className="menu-item__icon">{item.icon}</div>
        <div className="menu-item__title">{item.title}</div>
        <div className="menu-item__arrow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="client-profile">
      {/* Notifications */}
      {error && (
        <div className="tesla-alert tesla-alert--error">
          <span>{error}</span>
          <button className="tesla-alert__close" onClick={() => setError(null)}>
            <CloseIcon />
          </button>
        </div>
      )}
      
      {success && (
        <div className="tesla-toast">
          <span>{success}</span>
          <button className="tesla-toast__close" onClick={handleCloseSuccess}>
            <CloseIcon />
          </button>
        </div>
      )}
      
      <div className="profile-layout">
        {/* Left Sidebar - Menu Options */}
        <div className="profile-sidebar">
          {/* Profile Photo */}
          <div className="profile-photo-container">
            {user?.profilePhoto?.url ? (
              <div className="profile-photo">
                <img 
                  src={user.profilePhoto.url} 
                  alt={user.name} 
                />
                {photoLoading && <div className="tesla-loader"></div>}
              </div>
            ) : (
              <div className="profile-photo-placeholder">
                {user?.name.charAt(0).toUpperCase()}
                {photoLoading && <div className="tesla-loader"></div>}
              </div>
            )}
            <button 
              className="profile-photo-edit"
              onClick={() => fileInputRef.current?.click()}
              disabled={photoLoading}
            >
              <CameraIcon />
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
            />
            
            {user?.profilePhoto && (
              <button
                className="tesla-button tesla-button--outline tesla-button--sm mt-4"
                onClick={handleDeletePhoto}
                disabled={photoLoading}
              >
                <DeleteIcon />
                Remove Photo
              </button>
            )}
          </div>
          
          <div className="profile-name">{user?.name}</div>
          <div className="profile-type">Client</div>
          
          {/* Menu Sections */}
          <div className="menu-section">
            <div className="menu-section__title">Account Settings</div>
            {accountSettings.map(renderMenuItem)}
          </div>
          
          <div className="menu-section">
            <div className="menu-section__title">Help & Support</div>
            {helpSettings.map(renderMenuItem)}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="profile-content">
          <div className="profile-content__header">
            <h1 className="profile-content__title">My Profile</h1>
          </div>
          
          {/* Profile Information Card */}
          <div className="tesla-card">
            <div className="tesla-card__header">
              <h2 className="tesla-card__title">Profile Information</h2>
              <button
                className={`tesla-button ${editMode ? "tesla-button--primary" : "tesla-button--outline"}`}
                onClick={() => {
                  if (editMode) {
                    const formEl = document.querySelector('form') as HTMLFormElement;
                    formEl?.dispatchEvent(new Event('submit', { cancelable: true }));
                  } else {
                    setEditMode(true);
                  }
                }}
                disabled={loading}
              >
                {editMode ? <SaveIcon /> : <EditIcon />}
                {editMode ? 'Save Changes' : 'Edit Profile'}
              </button>
            </div>
            
            <div className="tesla-card__content">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="tesla-input"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-icon">
                    <EmailIcon />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="tesla-input"
                      value={formData.email}
                      disabled={true} // Email can only be changed in settings
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="input-with-icon">
                    <PhoneIcon />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="tesla-input"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!editMode || loading}
                    />
                  </div>
                </div>
                
                {editMode && (
                  <div className="form-actions">
                    <button
                      type="button"
                      className="tesla-button tesla-button--outline"
                      onClick={() => setEditMode(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    
                    <button
                      type="submit"
                      className="tesla-button tesla-button--primary"
                      disabled={loading}
                    >
                      {loading ? <div className="tesla-loader tesla-loader--sm"></div> : <SaveIcon />}
                      Save Changes
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
          
          {/* Loyalty Card */}
          <div className="tesla-card">
            <div className="tesla-card__header">
              <h2 className="tesla-card__title">
                <LoyaltyIcon /> Loyalty Program
              </h2>
            </div>
            
            <div className="tesla-card__content">
              <div className="loyalty-points">
                <div className="loyalty-points__value">
                  {(user as any)?.loyaltyPoints || 0}
                </div>
                <div className="loyalty-points__label">Available Points</div>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="tesla-modal">
          <div className="tesla-modal__backdrop" onClick={() => setShowLogoutConfirm(false)}></div>
          <div className="tesla-modal__container">
            <div className="tesla-modal__header">
              <h2 className="tesla-modal__title">Confirm Logout</h2>
              <button 
                className="tesla-modal__close"
                onClick={() => setShowLogoutConfirm(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="tesla-modal__content">
              <p>Are you sure you want to logout?</p>
            </div>
            <div className="tesla-modal__actions">
              <button 
                className="tesla-button tesla-button--outline"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="tesla-button tesla-button--primary"
                onClick={handleLogout}
              >
                {loading ? <div className="tesla-loader tesla-loader--sm"></div> : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;